import React from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Globe2 } from 'lucide-react';
import { countries } from '../mock';
import { Button } from '../components/ui/button';

export default function CountriesAndCTA({ onCreate }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const active = (params.get('country') || '').trim();

  const selectCountry = (name) => {
    // Toggle: clicking the active country clears the filter.
    const next = new URLSearchParams(params);
    if (active.toLowerCase() === name.toLowerCase()) {
      next.delete('country');
    } else {
      next.set('country', name);
    }
    // If we're not on the home page, jump home first so the campaigns section exists.
    if (location.pathname !== '/') {
      navigate(`/?${next.toString()}#cagnottes`);
      return;
    }
    setParams(next, { replace: false });
    // Smoothly scroll to the campaigns section after the URL updates.
    requestAnimationFrame(() => {
      const el = document.getElementById('cagnottes');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const exploreCagnottes = () => {
    const next = new URLSearchParams(params);
    next.delete('country');
    if (location.pathname !== '/') {
      navigate(`/?${next.toString()}#cagnottes`);
      return;
    }
    setParams(next, { replace: false });
    requestAnimationFrame(() => {
      const el = document.getElementById('cagnottes');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleCreate = () => {
    if (onCreate) {
      onCreate('Joyeuse');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <section id="countries" className="relative">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-medium text-neutral-500">
          <Globe2 className="w-3.5 h-3.5" /> Disponible dans
        </div>
        <h2 className="mt-2 text-[clamp(1.6rem,3vw,2.4rem)] leading-[1.1] font-semibold tracking-tight text-[#1a1d1a]">
          Présent partout en Afrique <span className="serif-italic text-[#5a8268]">&amp; dans la diaspora</span>
        </h2>
        <p className="mt-3 text-[13.5px] text-neutral-600 max-w-xl">
          Cliquez sur un pays pour afficher uniquement les cagnottes qui s'y déroulent.
        </p>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="country-pills">
          {countries.map((c) => {
            const isActive = active.toLowerCase() === c.name.toLowerCase();
            return (
              <button
                key={c.code}
                type="button"
                onClick={() => selectCountry(c.name)}
                aria-pressed={isActive}
                data-testid={`country-pill-${c.code}`}
                className={`btn-anim inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] transition-all border ${
                  isActive
                    ? 'bg-[#1a1d1a] text-white border-[#1a1d1a] shadow-sm'
                    : 'bg-white text-neutral-700 border-black/5 hover:bg-neutral-50 hover:border-black/10'
                }`}
              >
                <span aria-hidden="true">{c.flag}</span> {c.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Final CTA */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="relative rounded-[32px] overflow-hidden bg-gradient-to-br from-[#fff3ec] via-[#fdebd9] to-[#eaf1e7] border border-black/5 p-10 sm:p-16 text-center">
          <h2 className="text-[clamp(2.4rem,5.6vw,4.8rem)] leading-[1.02] font-semibold tracking-tight text-[#1a1d1a]">
            On est <span className="serif-italic text-[#d97757]">ensemble</span>.
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-[15.5px] text-neutral-700">
            Lancez votre première cagnotte aujourd&apos;hui. C&apos;est gratuit, c&apos;est solidaire, c&apos;est puissant.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              onClick={handleCreate}
              data-testid="cta-create-cagnotte-btn"
              className="btn-anim rounded-full h-12 px-6 bg-[#1a1d1a] hover:bg-black text-white text-[14.5px] font-medium"
            >
              Créer ma cagnotte
            </Button>
            <Button
              onClick={exploreCagnottes}
              variant="outline"
              data-testid="cta-explore-cagnottes-btn"
              className="btn-anim rounded-full h-12 px-6 bg-white text-[#1a1d1a] border-black/15 hover:bg-neutral-50 text-[14.5px]"
            >
              Explorer les cagnottes
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
